import React from 'react';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import KindergartenSwitcher from './KindergartenSwitcher';
import { useFinanceKindergarten } from '../useFinanceKindergarten';
import { FeatureCode } from '../../../common/enums/common.enum';
import { useKindergartenFeatures } from '../../../utilities/hooks/useKindergartenFeatures';

interface FinanceSectionPageProps {
  title: string;
  /** One line under the title saying what the page is for. */
  description: string;
  /** Where a kindergarten switch lands. Defaults to this same page for the new one. */
  kindergartenHref?: (kindergartenId: string) => string;
  /** Buttons on the right of the title. Told whether there is a usable kindergarten yet. */
  actions?: (context: { kindergartenId: string; ready: boolean }) => React.ReactNode;
  /** The platform catalogue belongs to no kindergarten, so it skips the switcher and the gate. */
  withoutKindergarten?: boolean;
  /** Page-specific wording for the two states every page has to explain. */
  messages?: { pickKindergarten?: string; invoicingOff?: string };
  /** Asked before a kindergarten switch, for a page that can hold unsaved edits. */
  guardNavigation?: (proceed: () => void) => void;
  children: (context: { kindergartenId: string; kindergartenName?: string }) => React.ReactNode;
}

/**
 * The frame every Finance section page shares: a title with one line of description, the
 * kindergarten it is about on the right, and the same three gates in the same order - no
 * kindergarten picked, its features still loading, invoicing switched off for it.
 *
 * No breadcrumb: these pages sit one level under Finance, which the sidebar already shows.
 * Only drilling down through the overview, and detail pages, have somewhere to go back to.
 *
 * Each page used to carry its own copy of those gates. Five copies are five places for the
 * wording, or the order, to drift; one frame makes "this kindergarten does not invoice"
 * read the same everywhere.
 *
 * The body is keyed by kindergarten: tabs hold filters, pages and selections that belong
 * to the one being left, and a switch should start them clean rather than carry them over.
 */
const FinanceSectionPage: React.FC<FinanceSectionPageProps> = ({
  title,
  description,
  kindergartenHref,
  actions,
  withoutKindergarten,
  messages,
  guardNavigation,
  children
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { kindergartenId, kindergartenName } = useFinanceKindergarten();
  const { isEnabled, isLoading: isLoadingFeatures } = useKindergartenFeatures(
    withoutKindergarten ? undefined : kindergartenId || undefined
  );
  const invoicingEnabled = isEnabled(FeatureCode.INVOICING);
  const ready = withoutKindergarten || (!!kindergartenId && !isLoadingFeatures && invoicingEnabled);

  const body = () => {
    if (withoutKindergarten) return children({ kindergartenId: '' });
    if (!kindergartenId) {
      return (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          {messages?.pickKindergarten ?? t('finance.pickKindergarten')}
        </Alert>
      );
    }
    if (isLoadingFeatures) {
      // a kindergarten is picked and its features are still arriving; saying "pick a
      // kindergarten" here would contradict the switcher right above it
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (!invoicingEnabled) {
      // said once, plainly, instead of letting every list below fail with a 403
      return (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          {messages?.invoicingOff ?? t('finance.invoicingOff')}
        </Alert>
      );
    }
    return <React.Fragment key={kindergartenId}>{children({ kindergartenId, kindergartenName })}</React.Fragment>;
  };

  return (
    <Box sx={{ p: 7 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h2">{title}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {description}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
          {actions?.({ kindergartenId, ready })}
          {!withoutKindergarten && (
            <KindergartenSwitcher
              kindergartenId={kindergartenId}
              href={kindergartenHref ?? (id => `${location.pathname}?kindergartenId=${id}`)}
              guard={guardNavigation}
            />
          )}
        </Stack>
      </Stack>
      {body()}
    </Box>
  );
};

export default FinanceSectionPage;
